/**
 * GET /api/billing/subscription/summary — plan, trial, and subscription status.
 */

import { isLegacyBillingUserType, isNewBillingScreenVariant } from './billingScreenVariant.js'

export const SUBSCRIPTION_SUMMARY_URL = '/api/billing/subscription/summary'
export const SUBSCRIPTION_RESUME_URL = '/api/billing/subscription/resume'

/**
 * Pro Annual card on Settings > Billing. Off until the live $600/yr Stripe price is set up
 * (docs/billing/pro-annual-plan-switch.md). Tenants already on Pro Annual still see their card.
 */
export const PRO_ANNUAL_UI_ENABLED = false

/** Full-screen trial lockout (blur, nav block, plan modal). Re-enable when ready to ship. */
export const TRIAL_LOCKOUT_ENABLED = false

/** Full-screen lockout when subscription has fully ended after cancellation. */
export const CANCELED_LOCKOUT_ENABLED = true

/** Grace after `subscriptionEndDate` / period end before past-due lockout (ms). */
export const PAST_DUE_LOCKOUT_GRACE_MS = 2 * 24 * 60 * 60 * 1000

/** Full-screen lockout when `past_due` and now is past period end + grace. */
export const PAST_DUE_LOCKOUT_ENABLED = true

function truthyFlag (value) {
  return value === true || value === 'true' || value === 1 || value === '1'
}

/** Normalize API body (camelCase or snake_case). */
export function normalizeSubscriptionSummary (body) {
  if (!body || typeof body !== 'object') return null
  var statusRaw = body.status != null ? body.status : ''
  return {
    planName: body.planName != null ? body.planName : (body.plan_name || ''),
    subscriptionEndDate:
      body.subscriptionEndDate != null
        ? body.subscriptionEndDate
        : body.subscription_end_date || '',
    subscriptionEndDateEpoch:
      body.subscriptionEndDateEpoch != null
        ? body.subscriptionEndDateEpoch
        : body.subscription_end_date_epoch,
    paymentInfoAdded: truthyFlag(
      body.paymentInfoAdded != null ? body.paymentInfoAdded : body.payment_info_added
    ),
    status: String(statusRaw).toLowerCase(),
    trialExpired: truthyFlag(
      body.trialExpired != null ? body.trialExpired : body.trial_expired
    ),
    trialActive: truthyFlag(
      body.trialActive != null ? body.trialActive : body.trial_active
    ),
    cancelAtPeriodEnd: truthyFlag(
      body.cancelAtPeriodEnd != null ? body.cancelAtPeriodEnd : body.cancel_at_period_end
    ),
    selectedPostTrialPlan:
      body.selectedPostTrialPlan != null
        ? body.selectedPostTrialPlan
        : body.selected_post_trial_plan || null,
    planKey: body.planKey != null ? body.planKey : (body.plan_key || ''),
    planLabel: body.planLabel != null ? body.planLabel : (body.plan_label || ''),
    modernPlan: truthyFlag(body.modernPlan != null ? body.modernPlan : body.modern_plan),
    billingInterval: body.billingInterval != null ? body.billingInterval : (body.billing_interval || null),
    planFeeCents: optionalNumber(body.planFeeCents != null ? body.planFeeCents : body.plan_fee_cents),
    /** null = Pro Annual not offered (package or Stripe price not set up). */
    proAnnualPriceCents: optionalNumber(
      body.proAnnualPriceCents != null ? body.proAnnualPriceCents : body.pro_annual_price_cents
    )
  }
}

function optionalNumber (value) {
  if (value == null || value === '' || isNaN(Number(value))) return null
  return Number(value)
}

export function hasPostTrialPlanSelected (summary) {
  return selectedPostTrialPlanKeyFromSummary(summary) != null
}

/** User-chosen post-trial plan from summary (`starter` | `pro` | `pro_annual`). */
export function selectedPostTrialPlanKeyFromSummary (summary) {
  if (!summary) return null
  var raw = summary.selectedPostTrialPlan
  if (raw == null || raw === '') return null
  var s = String(raw).toLowerCase().trim()
  if (s === 'starter' || s === 'pro' || s === 'pro_annual') return s
  if (s.indexOf('annual') >= 0) return 'pro_annual'
  if (s.indexOf('pro') >= 0) return 'pro'
  if (s.indexOf('starter') >= 0) return 'starter'
  return null
}

/**
 * Plan to highlight in trial UI — always prefers API selection over volume recommendation.
 * @param {object|null} summary
 * @param {'starter'|'pro'|null} volumeRecommendedKey
 */
export function highlightedTrialPlanKey (summary, volumeRecommendedKey) {
  var selected = selectedPostTrialPlanKeyFromSummary(summary)
  if (selected) return selected
  return volumeRecommendedKey || null
}

/**
 * Plan to show as selected in UI — API pick, else infer from current plan name.
 * @param {object|null} summary
 * @param {boolean} isProPlan — from summary name and/or package details
 */
export function displaySelectedPlanKeyFromContext (summary, isProPlan) {
  var picked = selectedPostTrialPlanKeyFromSummary(summary)
  if (picked) return picked
  if (isProPlan) return 'pro'
  if (summary && planNameFromSummary(summary)) return 'starter'
  return null
}

export function trialDaysRemainingFromSummary (summary) {
  if (!summary) return null
  var epoch = subscriptionEndEpochFromSummary(summary)
  if (epoch == null) return null
  var endMs = epoch * 1000
  var diff = endMs - Date.now()
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)))
}

/** Stripe subscription is current — trial flags are ignored. */
export function isSubscriptionStatusActive (summary) {
  if (!summary || !summary.status) return false
  return String(summary.status).toLowerCase() === 'active'
}

/** Last invoice failed; subscription needs an updated payment method. */
export function isSubscriptionStatusPastDue (summary) {
  if (!summary || !summary.status) return false
  return String(summary.status).toLowerCase() === 'past_due'
}

/**
 * Lockout starts at subscription period end (`subscriptionEndDate` / epoch) + grace (2 days).
 * @returns {number|null} Unix seconds when lockout begins
 */
export function pastDueLockoutEpochFromSummary (summary, graceMs) {
  if (!isSubscriptionStatusPastDue(summary)) return null
  var endEpoch = subscriptionEndEpochFromSummary(summary)
  if (endEpoch == null) return null
  var graceSec = Math.floor((graceMs != null ? graceMs : PAST_DUE_LOCKOUT_GRACE_MS) / 1000)
  return endEpoch + graceSec
}

/** Ms remaining until past-due lockout; 0 if lockout time has passed. */
export function millisUntilPastDueLockout (summary, graceMs) {
  var lockoutEpoch = pastDueLockoutEpochFromSummary(summary, graceMs)
  if (lockoutEpoch == null) return null
  return Math.max(0, lockoutEpoch * 1000 - Date.now())
}

/** True when `past_due` and now is at or after period end + 2 days. */
export function isPastDueGracePeriodElapsed (summary, graceMs) {
  if (!isSubscriptionStatusPastDue(summary)) return false
  var lockoutEpoch = pastDueLockoutEpochFromSummary(summary, graceMs)
  if (lockoutEpoch == null) return false
  return Date.now() >= lockoutEpoch * 1000
}

/** True when summary reports a saved payment method (card on file). */
export function hasPaymentMethodFromSummary (summary) {
  return !!(summary && summary.paymentInfoAdded)
}

/** Stripe subscription fully canceled (access ended). */
export function isSubscriptionStatusCanceled (summary) {
  if (!summary || !summary.status) return false
  var s = String(summary.status).toLowerCase()
  return s === 'canceled' || s === 'cancelled'
}

/**
 * User cancelled but is still within the paid period (`cancel_at_period_end`).
 * They should not be locked out until the period ends.
 */
export function isSubscriptionCancelScheduledActive (summary) {
  if (!summary || !summary.cancelAtPeriodEnd) return false
  if (isSubscriptionStatusCanceled(summary)) return false
  return isSubscriptionStatusActive(summary) || isSubscriptionStatusPastDue(summary)
}

/** App-shell lockout when subscription ended after cancellation (or legacy paymentStatus 403). */
export function shouldShowCanceledLockout (summary, paymentStatus) {
  if (!CANCELED_LOCKOUT_ENABLED) return false
  if (isSubscriptionCancelScheduledActive(summary)) return false
  if (summary && isSubscriptionStatusActive(summary) && !isSubscriptionStatusCanceled(summary)) {
    return false
  }
  if (paymentStatus === 403) {
    if (!summary) return true
    return isSubscriptionStatusCanceled(summary)
  }
  if (!isStarterOrProBillingTenant(summary)) return false
  if (!summary) return false
  if (isSubscriptionStatusPastDue(summary)) return false
  return isSubscriptionStatusCanceled(summary)
}

/** Copy for canceled lockout overlay. */
export function canceledLockoutCopyFromSummary (summary) {
  var endLabel = formatSubscriptionEndDate(summary)
  var when =
    endLabel !== '—'
      ? ' Your access ended on <strong>' + endLabel + '</strong>.'
      : ''
  return {
    title: 'Your subscription has ended',
    messageHtml:
      'You cancelled your SentryKit subscription.' +
      when +
      ' Dashboard access, real-time alerts, and Buy Box monitoring are paused. Your data stays in your account — contact support if you need help.'
  }
}

/** Copy for past-due lockout overlay (after grace period). */
export function pastDueLockoutCopyFromSummary (summary) {
  return {
    title: 'Payment required',
    messageHtml:
      '<strong>Your payment failed more than 2 days ago.</strong> Dashboard access and alerts are paused until you update your payment method. Your plan and data are unchanged.'
  }
}

/** App-shell lockout when `past_due` exceeds the grace window. */
export function shouldShowPastDueLockout (summary, billingOpts) {
  if (!PAST_DUE_LOCKOUT_ENABLED) return false
  if (!summary || !isSubscriptionStatusPastDue(summary)) return false
  if (shouldShowCanceledLockout(summary, null)) return false
  billingOpts = billingOpts || {}
  if (isLegacyBillingUserType(billingOpts)) return false
  if (!isNewBillingScreenVariant(billingOpts)) return false
  if (!isStarterOrProBillingTenant(summary)) return false
  return isPastDueGracePeriodElapsed(summary)
}

/** Top strip while cancel is scheduled but access continues until period end. */
export function scheduledCancelStripContentFromSummary (summary) {
  if (!isSubscriptionCancelScheduledActive(summary)) return null
  var endLabel = formatSubscriptionEndDate(summary)
  return {
    tone: 'amber',
    messageHtml:
      '<strong>Subscription ends ' +
      endLabel +
      '.</strong> You still have full access until then. Resume your subscription anytime to keep alerts running.',
    ctaLabel: 'Resume subscription →',
    ctaHash: '',
    ctaAction: 'resume',
    dismissible: true
  }
}

/** App-shell lockout when trial expired and not on a paid active subscription. */
export function shouldShowTrialLockout (summary, billingOpts) {
  if (!TRIAL_LOCKOUT_ENABLED) return false
  if (shouldShowCanceledLockout(summary, null)) return false
  if (!isNewBillingScreenVariant(billingOpts || {})) return false
  if (!summary) return false
  if (isSubscriptionStatusActive(summary)) return false
  if (isSubscriptionStatusPastDue(summary)) return false
  return isTrialExpiredFromSummary(summary)
}

/**
 * Urgency strip above app shell. Returns null when hidden.
 * @returns {{ tone: string, messageHtml: string, ctaLabel: string, ctaHash: string, dismissible: boolean } | null}
 */
export function trialStripContentFromSummary (summary, billingOpts) {
  billingOpts = billingOpts || {}
  var scheduledCancel = scheduledCancelStripContentFromSummary(summary)
  if (scheduledCancel) return scheduledCancel
  if (isLegacyBillingUserType(billingOpts)) return null
  if (!isNewBillingScreenVariant(billingOpts)) return null
  if (!summary) return null
  if (isSubscriptionStatusActive(summary)) return null

  if (isSubscriptionStatusPastDue(summary)) {
    if (shouldShowPastDueLockout(summary, billingOpts)) return null
    var daysLeft = 2
    var untilLockout = millisUntilPastDueLockout(summary)
    if (untilLockout != null && untilLockout > 0) {
      daysLeft = Math.max(1, Math.ceil(untilLockout / (24 * 60 * 60 * 1000)))
    }
    return {
      tone: 'past-due',
      messageHtml:
        '<strong>Payment failed.</strong> Update your payment method within ' +
        daysLeft +
        ' day' +
        (daysLeft === 1 ? '' : 's') +
        ' to avoid losing dashboard access.',
      ctaLabel: 'Update payment method →',
      ctaHash: '#payment-method',
      dismissible: false
    }
  }

  var endLabel = formatSubscriptionEndDate(summary)
  var days = trialDaysRemainingFromSummary(summary)

  if (isTrialExpiredFromSummary(summary)) {
    return {
      tone: 'expired',
      messageHtml:
        '<strong>Your trial has ended.</strong> Pick a plan to resume service — your data is safe.',
      ctaLabel: 'Pick a plan now →',
      ctaHash: '',
      dismissible: false
    }
  }

  if (!isTrialActiveFromSummary(summary)) return null

  if (summary.paymentInfoAdded && hasPostTrialPlanSelected(summary)) return null
  if (days != null && days > 7) return null

  if (days === 0) {
    return {
      tone: 'red',
      messageHtml:
        '<strong>Your trial ends today.</strong> Complete setup now or your alerts and dashboard access will pause at midnight.',
      ctaLabel: 'Complete setup →',
      ctaHash: '',
      dismissible: true
    }
  }

  if (days != null && days >= 1 && days <= 3) {
    if (!summary.paymentInfoAdded) {
      return {
        tone: 'orange',
        messageHtml:
          '<strong>' +
          days +
          (days === 1 ? ' day' : ' days') +
          ' left.</strong> Add a payment method to keep your access after ' +
          endLabel +
          ' — no charge until then.',
        ctaLabel: 'Add payment method →',
        ctaHash: '#payment-method',
        dismissible: true
      }
    }
    return {
      tone: 'orange',
      messageHtml:
        '<strong>' +
        days +
        (days === 1 ? ' day' : ' days') +
        ' left.</strong> Pick a plan to keep your access after ' +
        endLabel +
        '.',
      ctaLabel: 'Pick your plan →',
      ctaHash: '',
      dismissible: true
    }
  }

  if (days != null && days >= 4 && days <= 7) {
    return {
      tone: 'amber',
      messageHtml:
        '<strong>' +
        days +
        ' days left in your trial.</strong> Pick a plan to keep your alerts running after ' +
        endLabel +
        '.',
      ctaLabel: 'Pick your plan →',
      ctaHash: '',
      dismissible: true
    }
  }

  return null
}

export function shouldShowTrialStrip (summary, dismissed, billingOpts) {
  billingOpts = billingOpts || {}
  if (shouldShowPastDueLockout(summary, billingOpts)) return false
  var content = trialStripContentFromSummary(summary, billingOpts)
  if (!content) return false
  if (dismissed && content.dismissible) return false
  return true
}

/** User is in an active free trial — not when `active` or `past_due`. */
export function isTrialActiveFromSummary (summary) {
  if (!summary) return false
  if (isSubscriptionStatusActive(summary)) return false
  if (isSubscriptionStatusPastDue(summary)) return false
  if (summary.trialExpired) return false
  return summary.trialActive === true
}

/** Trial ended — not when `active` or `past_due`. */
export function isTrialExpiredFromSummary (summary) {
  if (!summary) return false
  if (isSubscriptionStatusActive(summary)) return false
  if (isSubscriptionStatusPastDue(summary)) return false
  return summary.trialExpired === true
}

/** Paid subscription (not trialing). */
export function isPaidActiveFromSummary (summary) {
  if (!summary) return false
  if (isTrialActiveFromSummary(summary)) return false
  if (isTrialExpiredFromSummary(summary)) return false
  return summary.status === 'active'
}

export function planNameFromSummary (summary) {
  if (!summary || !summary.planName) return ''
  return String(summary.planName).trim()
}

export function planKeyFromSummary (summary) {
  if (!summary) return ''
  var raw = summary.planKey != null ? summary.planKey : summary.plan_key
  return String(raw == null ? '' : raw).trim().toUpperCase()
}

export function isFlatYearlyPlanFromSummary (summary) {
  if (!summary) return false
  if (planKeyFromSummary(summary) === 'FLAT_YEARLY') return true
  if (summary.tier === 'flat_yearly' || summary.planTier === 'flat_yearly') return true
  return planNameFromSummary(summary).toLowerCase().indexOf('flat yearly') >= 0
}

export function isProAnnualPlanFromSummary (summary) {
  if (!summary) return false
  if (planKeyFromSummary(summary) === 'PRO_ANNUAL') return true
  return planNameFromSummary(summary).toLowerCase().indexOf('pro annual') >= 0
}

/**
 * Plan the tenant is on now: `starter` | `pro` | `pro_annual` | null (legacy / flat yearly / unknown).
 * During a trial this is the plan that starts billing when the trial ends.
 */
export function currentPlanKeyFromSummary (summary) {
  if (!summary) return null
  if (isFlatYearlyPlanFromSummary(summary)) return null
  if (isProAnnualPlanFromSummary(summary)) return 'pro_annual'
  if (isProPlanFromSummary(summary)) return 'pro'
  if (planKeyFromSummary(summary) === 'STARTER' || isStarterPlanFromSummary(summary)) return 'starter'
  return null
}

export function isProPlanFromSummary (summary) {
  if (isFlatYearlyPlanFromSummary(summary)) return false
  var key = planKeyFromSummary(summary)
  if (key === 'PRO' || key === 'PRO_ANNUAL') return true
  var name = planNameFromSummary(summary).toLowerCase()
  if (!name) return false
  return name.indexOf('pro') >= 0
}

export function isStarterPlanFromSummary (summary) {
  var name = planNameFromSummary(summary).toLowerCase()
  if (!name) return false
  return name.indexOf('starter') >= 0
}

/**
 * Trial strip / lockout applies when summary `planName` is Starter or Pro,
 * OR when the account is in a trial/expired-trial state with no legacy plan name.
 * Legacy/daily users are excluded.
 */
export function isStarterOrProBillingTenant (summary) {
  if (!summary) return false
  var plan = planNameFromSummary(summary).toLowerCase()
  // If plan name is explicitly set, trust it
  var key = planKeyFromSummary(summary)
  if (key === 'STARTER' || key === 'PRO' || key === 'PRO_ANNUAL' || key === 'FLAT_YEARLY') return true
  if (summary.modernPlan === true) return true
  if (plan) {
    if (plan.indexOf('legacy') >= 0 || plan.indexOf('daily') >= 0 || plan.indexOf('meter') >= 0) {
      return false
    }
    if (plan.indexOf('flat yearly') >= 0 || plan.indexOf('starter') >= 0 || plan.indexOf('pro') >= 0) return true
    // Any other named plan (e.g. "Administrator") — not a Starter/Pro billing tenant
    return false
  }
  // No plan name — if in trial or expired trial, treat as Starter/Pro candidate
  var status = String(summary.status || '').toLowerCase()
  if (status === 'trialing' || summary.trialActive || summary.trialExpired) return true
  return false
}

export function subscriptionEndEpochFromSummary (summary) {
  if (!summary) return null
  var epoch = summary.subscriptionEndDateEpoch
  if (epoch != null && epoch !== '' && !isNaN(Number(epoch))) return Number(epoch)
  var iso = summary.subscriptionEndDate
  if (!iso) return null
  var ms = Date.parse(String(iso))
  if (isNaN(ms)) return null
  return Math.floor(ms / 1000)
}

export function formatSubscriptionEndDate (summary, formatter) {
  if (!summary) return '—'
  var iso = summary.subscriptionEndDate
  if (iso) {
    var d = new Date(String(iso))
    if (!isNaN(d.getTime())) {
      if (typeof formatter === 'function') return formatter(d)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
  var epoch = subscriptionEndEpochFromSummary(summary)
  if (epoch != null) {
    var d2 = new Date(epoch * 1000)
    if (!isNaN(d2.getTime())) {
      if (typeof formatter === 'function') return formatter(d2)
      return d2.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
  return '—'
}



// WEBPACK FOOTER //
// ./src/utils/subscriptionBilling.js